/**
 * @fileoverview
 * Table Component - Reusable table component with pagination, search, filters, and menu
 * Migrated to new architecture with OnPush change detection
 *
 * @version 1.0.0
 * @author KATALYST TEAM
 */

import { CommonModule } from '@angular/common';
import {
  Component,
  ContentChild,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  QueryList,
  SimpleChanges,
  TemplateRef,
  ViewChild,
  ViewChildren,
  AfterViewInit,
  ChangeDetectorRef,
  ChangeDetectionStrategy,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router, RouterModule } from '@angular/router';

import { TableRow } from './table.model';
import { SearchComponent } from '../search/search.component';
import { ActionButtonComponent } from '../action-button/action-button.component';
import { DropdownComponent } from '../dropdown/dropdown.component';
import { CameraCaptureModalComponent } from '../camera-capture-modal/camera-capture-modal.component';
import { HasPermissionDirective } from '@core/authentication';
import { AuthService } from '@core/authentication';
import { ClickOutsideDirective } from '@core/directives/click-outside.directive';
import { inject } from '@angular/core';

export interface TableColumn {
  key: string;
  header: string;
  alignRight?: boolean; // Optional: Set true for right-aligned columns
  linkRoute?: string | ((row: any) => string); // Optional: Route or function to generate route for clickable column
  sortable?: boolean; // Optional: Enable/disable sorting for this column (default: true, except for 'menu' and empty key)
  showTooltip?: boolean; // Optional: Show tooltip on hover – cell shows first N chars (default 50), full text in tooltip
  tooltipTruncateLength?: number; // Optional: character count when showTooltip is true (default: 50)
  wrapText?: boolean; // Optional: Allow long text to wrap in cell (avoids horizontal scroll, no tooltip)

  // Column type for editable columns
  type?: 'text' | 'dropdown' | 'input' | 'image' | 'badge' | 'action';

  /** When true, column is read-only (no editing) */
  readOnly?: boolean;

  // Dropdown configuration (when type is 'dropdown')
  dropdownOptions?: string[];  // Options for dropdown
  placeholder?: string;        // Placeholder text

  // Conditional disable based on another field's value
  disableWhen?: {
    field: string;             // Field key to check
    value: string;             // Value that triggers disable
  };

  // Clear this field when another field changes to a specific value
  clearWhen?: {
    field: string;             // Field key to watch
    value: string;             // Value that triggers clear
  };
}

@Component({
  selector: 'app-table',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    SearchComponent,
    RouterModule,
    ActionButtonComponent,
    DropdownComponent,
    CameraCaptureModalComponent,
    HasPermissionDirective,
    ClickOutsideDirective,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './table.component.html',
  styleUrl: './table.component.css',
})
export class TableComponent implements OnInit, AfterViewInit, OnChanges {
  @Input() columns: TableColumn[] = [];
  @Input() childRowColumns?: TableColumn[]; // Separate columns for child rows (sub-table)
  @Input() rows: TableRow[] = [];
  @Input() footerRow: TableRow | null = null;
  @Input() showPagination: boolean = true;
  @Input() showSearch: boolean = true;
  @Input() showFilters: boolean = true;
  @Input() showCard: boolean = true;
  /** When true, table container uses h-screen (e.g. asset audit) so dropdowns are not clipped when few rows. */
  @Input() tableContainerFullHeight: boolean = false;
  @Input() showMenu: boolean = false; // Default false - only masters components should set this to true
  @Input() showEdit: boolean = false; // If true, shows only edit action (no delete) - only checks editPermission
  @Input() showDelete: boolean = true; // When false, hide delete action in menu
  /** When readonly, show row menu (Edit/Delete) for rows where this returns true (e.g. pending vault rows in view-only). */
  @Input() showMenuWhenReadonly?: (row: TableRow) => boolean;
  // Server-side pagination inputs
  @Input() serverSidePagination: boolean = false;
  @Input() serverTotalRecords: number = 0;
  @Input() serverCurrentPage: number = 1;
  @Input() serverPageSize: number = 10;
  @Output() edit = new EventEmitter<any>();
  @Output() delete = new EventEmitter<any>();
  @Output() pageChange = new EventEmitter<number>();
  @Output() pageSizeChange = new EventEmitter<number>();
  @Output() sortChange = new EventEmitter<{
    column: string;
    direction: 'asc' | 'desc';
  }>();
  @Output() searchChange = new EventEmitter<string>();
  @Output() picViewClick = new EventEmitter<{
    row: TableRow;
    imageData: any;
    rowIndex?: number;
  }>();
  /** When set, the View button in the picView column for this row index shows loading state. */
  @Input() picViewLoadingRowIndex: number | null = null;
  @Output() videoViewClick = new EventEmitter<{
    row: TableRow;
    videoData: any;
    rowIndex?: number;
  }>();
  /** When set, the View button in the videoView column for this row index shows loading state. */
  @Input() videoViewLoadingRowIndex: number | null = null;
  @Output() itemsImageClick = new EventEmitter<{
    row: TableRow;
    imageUrl: string;
  }>();
  @Output() startAuditClick = new EventEmitter<TableRow>();
  @Output() viewClick = new EventEmitter<TableRow>();
  @Output() viewImageClick = new EventEmitter<TableRow>();
  /** Emitted when OK / Not OK response button is clicked so parent can sync counts (e.g. userEdits). */
  @Output() responseChange = new EventEmitter<{ row: TableRow; value: string }>();
  @Output() inProgressAuditClick = new EventEmitter<TableRow>();
  @Output() deployClick = new EventEmitter<TableRow>();
  @Output() updateFormClick = new EventEmitter<TableRow>();
  /** Emitted when "Add" child row is clicked (e.g. Add Key). Payload: parentRow, parentIndex */
  @Output() addRowClick = new EventEmitter<{ parentRow: TableRow; parentIndex: number }>();
  /** Emitted when a child row remove/delete is clicked. Payload: parentRow, parentIndex, childIndex; childRow when confirmBeforeRemoveChildRow is true */
  @Output() removeRowClick = new EventEmitter<{ parentRow: TableRow; parentIndex: number; childIndex: number; childRow?: TableRow }>();
  /** When true, table emits removeRowClick with childRow and does not splice; parent shows confirm then removes */
  @Input() confirmBeforeRemoveChildRow: boolean = false;
  /** Emitted when save is clicked on a child row. Payload: parentRow, parentIndex, childRow, childIndex */
  @Output() saveChildRowClick = new EventEmitter<{ parentRow: TableRow; parentIndex: number; childRow: TableRow; childIndex: number }>();
  /** Emitted when edit is clicked on a child row. Payload: parentRow, parentIndex, childRow, childIndex */
  @Output() editChildRowClick = new EventEmitter<{ parentRow: TableRow; parentIndex: number; childRow: TableRow; childIndex: number }>();
  @ViewChildren('dropdownMenu') dropdownMenus!: QueryList<ElementRef>;
  @ViewChild('headerTable') headerTable!: ElementRef;
  @ViewChild('tableContainer') tableContainer!: ElementRef<HTMLDivElement>;
  @ViewChild(CameraCaptureModalComponent) cameraModal!: CameraCaptureModalComponent;
  @ContentChild('idTemplate') idTemplate!: TemplateRef<any>;
  @Output() idClicked: EventEmitter<string> = new EventEmitter<string>();

  // Generic field change event - emits for any editable field (dropdown or input)
  @Output() fieldChanged = new EventEmitter<{
    row: TableRow;
    field: string;
    value: string;
    index: number;
    column: TableColumn;
  }>();

  @Input() onIdClicked!: ((id: any) => void) | null;
  @Input() useRouterNavigate: boolean = false;
  @Input() editPermission?: string; // Permission required for edit action
  @Input() deletePermission?: string; // Permission required for delete action
  /** When set, used for child rows instead of editPermission (use '' to show edit for child rows without permission). */
  @Input() childEditPermission?: string;
  /** When set, used for child rows instead of deletePermission (use '' to show delete for child rows without permission). */
  @Input() childDeletePermission?: string;
  @Input() columnClickHandlers?: { [key: string]: (row: any) => void }; // Custom click handlers for specific columns
  @Input() availabilityOptions: string[] = ['Available', 'Not Available'];
  @Input() statusOptions: string[] = ['Proper', 'Not Proper'];
  @Input() showAddRemoveButtons: boolean = false;
  private _isSecurityStatusTable = false;
  @Input() get isSecurityStatusTable(): boolean {
    return this._isSecurityStatusTable;
  }
  set isSecurityStatusTable(value: boolean) {
    this._isSecurityStatusTable = value;
  }
  @Input() customChildRowFactory?: (parentRow: TableRow) => TableRow;
  @Input() skipDefaultChildRow: boolean = false;
  @Input() enableExpandableRows: boolean = false;
  @Input() expandDetailTemplate: TemplateRef<{ $implicit: TableRow }> | null = null;
  @Input() expandButtonStyle: 'keys' | 'icon' = 'keys';
  /** When set, the expand/collapse icon is rendered inside this column (e.g. 'parameters') instead of a separate column. */
  @Input() expandInColumnKey?: string;
  @Input() readOnlyChildRows: boolean = false;
  @Input() autoExpandAllRows: boolean = false;
  @Input() readOnlyRemarks: boolean = false;
  @Input() showDeleteButtonForChildRows: boolean = true;
  /** When true, hide the green check (save) icon in child row actions column (e.g. Key Audit Key Verification only). */
  @Input() hideChildRowSaveIcon: boolean = false;
  @Input() readonly: boolean = false; // Readonly mode - disables all editing
  /** When readonly, show Add Key (+) for rows where this returns true (e.g. pending vault rows in view-only). */
  @Input() showAddKeyWhenReadonly?: (row: TableRow) => boolean;
  /** When readOnlyChildRows is true, show Edit/Remove for child rows where this returns true (e.g. keys under pending vault in view-only). */
  @Input() showChildRowActionsWhenReadonly?: (parentRow: TableRow, childRow: TableRow) => boolean;
  /** When set, the Start/Resume/View audit button for the row with this id shows loading state */
  @Input() loadingActionRowId: string | null = null;

  // Function to determine which menu actions to show for each row
  // Returns { showEdit: boolean, showDelete: boolean }
  // If not provided, defaults based on showMenu/showEdit inputs (backward compatibility)
  @Input() getMenuActions?: (row: TableRow) => { showEdit: boolean; showDelete: boolean };

  /** Columns to render; when readonly, excludes the 'actions' column; when expandInColumnKey is set, excludes the 'expand' column. */
  get visibleColumns(): TableColumn[] {
    let cols = this.columns;
    if (this.readonly) {
      cols = cols.filter((c) => c.key !== 'actions');
    }
    if (this.expandInColumnKey) {
      cols = cols.filter((c) => c.key !== 'expand');
    }
    return cols;
  }

  sortedColumn: string | null = null;
  sortDirection: 'asc' | 'desc' = 'asc';

  // Server-side sorting support
  @Input() serverSideSorting: boolean = false;
  @Input() currentSortColumn: string | null = null;
  @Input() currentSortDirection: 'asc' | 'desc' = 'asc';

  // Search value for syncing with parent
  @Input() searchValue: string = '';

  // External validation errors from parent component
  @Input() set externalValidationErrors(errors: Map<number, { [field: string]: string }> | null) {
    if (errors) {
      this.validationErrors = errors;
    } else {
      this.validationErrors = new Map();
    }
  }

  entries = [10, 25, 50, 100];
  selectedEntries = 10;
  currentPage = 1;
  totalRecords = 100;
  editRuleModal: boolean = false;
  entriesPerPage = 10; // Default rows per page
  paginatedRows: TableRow[] = [];
  openMenuIndex: number | null = null;
  dropdownDirection: { [key: number]: 'up' | 'down' } = {}; // Store dropdown position per row
  // Track which rows should have dropdowns opening upward (for availability and registerStatus)
  rowDropdownOpenUpward: Map<number, boolean> = new Map();
  expandedRows: Set<string> = new Set(); // Track expanded parent rows by unique identifier
  selectedId: string | null = null;
  selectedColor: string = '#E5E7EB';
  selectedDecoration: string = 'none';

  // Tooltip state
  tooltipVisible = false;
  tooltipText = '';
  tooltipX = 0;
  tooltipY = 0;

  // Validation errors tracking: Map<rowIndex, fieldErrors>
  private validationErrors: Map<number, { [field: string]: string }> = new Map();

  private authService = inject(AuthService);

  constructor(
    private router: Router,
    private elementRef: ElementRef,
    private cdr: ChangeDetectorRef
  ) { }

  /**
   * Flatten rows so each parent is followed by its child rows.
   * Ensures display indices match between table DOM and parent validation (e.g. security-status).
   */
  private flattenRowsWithChildren(rows: TableRow[]): TableRow[] {
    const flat: TableRow[] = [];
    (rows || []).forEach((row) => {
      flat.push(row);
      const childRows = row['_childRows'] as TableRow[] | undefined;
      if (childRows?.length) flat.push(...childRows);
    });
    return flat;
  }

  ngOnInit(): void {
    if (this.showPagination) {
      if (this.serverSidePagination) {
        this.paginatedRows = this.flattenRowsWithChildren(this.rows);
      } else {
        // Client-side pagination: paginate locally
        this.updatePagination();
      }
    } else {
      this.paginatedRows = this.rows;
    }
    if (typeof this.onIdClicked === 'function') {
      const functionString = this.onIdClicked.toString();
      this.useRouterNavigate = functionString.includes('.navigate(');
    }

    // Watch for selectedEntries changes to update entriesPerPage
    // Using a simple approach - will be updated via ngModelChange in template
  }

  toggleMenu(index: number) {
    // Toggle menu state
    this.openMenuIndex = this.openMenuIndex === index ? null : index;

    // If opening, adjust dropdown position
    if (this.openMenuIndex !== null) {
      setTimeout(() => this.adjustDropdownPosition(index), 0); // Ensure dropdown exists before calculating position
    }
  }
  adjustDropdownPosition(index: number): void {
    const dropdownArray = this.dropdownMenus.toArray();
    if (!dropdownArray[index]) return; // Ensure dropdown exists

    const dropdown = dropdownArray[index].nativeElement;
    const triggerElement = dropdown.closest('td');

    if (!dropdown || !triggerElement) return;

    const dropdownHeight = dropdown.offsetHeight;
    const rect = triggerElement.getBoundingClientRect();
    const spaceBelow = window.innerHeight - rect.bottom;
    const spaceAbove = rect.top;

    this.dropdownDirection[index] =
      spaceBelow >= dropdownHeight ? 'down' : 'up';
  }
  closeMenu(index: number) {
    this.openMenuIndex = this.openMenuIndex === index ? null : index;
  }
  closeMenuOnOutsideClick(index: number): void {
    if (this.openMenuIndex === index) {
      this.openMenuIndex = null;
    }
  }

  /** Update paginated rows based on current page */
  updatePagination() {
    // Apply sorting before pagination
    const sortedRows = this.getSortedRows();
    const startIndex = (this.currentPage - 1) * this.entriesPerPage;
    const endIndex = startIndex + this.entriesPerPage;
    const paginatedParentRows = sortedRows.slice(startIndex, endIndex);

    // Auto-expand all rows if autoExpandAllRows is enabled
    if (this.autoExpandAllRows && this.enableExpandableRows) {
      sortedRows.forEach((row) => {
        const rowKey = this.getRowKey(row);
        if (!this.expandedRows.has(rowKey)) {
          this.expandedRows.add(rowKey);
        }
      });
    }

    // Insert child rows after their parent rows (skip when using custom expand detail template)
    this.paginatedRows = [];
    paginatedParentRows.forEach((row) => {
      this.paginatedRows.push(row);

      // Add child rows if expandable rows are enabled, they exist, parent is expanded, and we're not using custom detail template
      if (this.enableExpandableRows && !this.expandDetailTemplate) {
        const rowKey = this.getRowKey(row);
        const isExpanded = this.expandedRows.has(rowKey);
        const childRows = row['_childRows'] as TableRow[] | undefined;
        if (childRows && childRows.length > 0 && isExpanded) {
          this.paginatedRows.push(...childRows);
        }
      } else if (!this.enableExpandableRows) {
        // For non-expandable tables, always show child rows if they exist (backward compatibility)
        const childRows = row['_childRows'] as TableRow[] | undefined;
        if (childRows && childRows.length > 0) {
          this.paginatedRows.push(...childRows);
        }
      }
    });
  }

  /** Get a unique key for a row (used for tracking expansion state) */
  getRowKey(row: TableRow): string {
    if (row['vaultName'] && row['modelRefNo']) {
      return `${row['vaultName']}_${row['modelRefNo']}`;
    }
    const index = this.rows.findIndex((r) => r === row);
    return `row_${index}`;
  }

  /** Toggle expansion of a parent row */
  toggleRowExpansion(row: TableRow): void {
    if (!this.enableExpandableRows || this.isChildRow(row)) {
      return;
    }
    const rowKey = this.getRowKey(row);
    if (this.expandedRows.has(rowKey)) {
      this.expandedRows.delete(rowKey);
    } else {
      this.expandedRows.add(rowKey);
    }
    this.updatePagination();
    this.cdr.detectChanges();
  }

  /** Check if a parent row is expanded */
  isRowExpanded(row: TableRow): boolean {
    if (!this.enableExpandableRows || this.isChildRow(row)) {
      return false;
    }
    const rowKey = this.getRowKey(row);
    return this.expandedRows.has(rowKey);
  }

  /** Check if a row has child rows or expandable detail */
  hasChildRows(row: TableRow): boolean {
    const childRows = row['_childRows'] as TableRow[] | undefined;
    if (childRows !== undefined && childRows.length > 0) return true;
    const observations = row['observations'] as unknown[] | undefined;
    return observations !== undefined && observations.length > 0;
  }

  /** Get the count of child rows for a parent row */
  getChildRowCount(row: TableRow): number {
    const childRows = row['_childRows'] as TableRow[] | undefined;
    return childRows ? childRows.length : 0;
  }

  /** Expand all parent rows (used when autoExpandAllRows is enabled) */
  expandAllRows(): void {
    const sortedRows = this.getSortedRows();
    sortedRows.forEach((row) => {
      const rowKey = this.getRowKey(row);
      this.expandedRows.add(rowKey);
    });
  }

  /** Get sorted rows based on current sort column and direction */
  getSortedRows(): TableRow[] {
    if (!this.sortedColumn) {
      return [...this.rows];
    }

    return [...this.rows].sort((a, b) => {
      const aValue = a[this.sortedColumn!];
      const bValue = b[this.sortedColumn!];

      // Handle null/undefined values
      if (aValue == null && bValue == null) return 0;
      if (aValue == null) return 1;
      if (bValue == null) return -1;

      // Compare values
      if (typeof aValue === 'string' && typeof bValue === 'string') {
        const comparison = aValue.localeCompare(bValue, undefined, {
          numeric: true,
          sensitivity: 'base',
        });
        return this.sortDirection === 'asc' ? comparison : -comparison;
      }

      // For numbers
      if (typeof aValue === 'number' && typeof bValue === 'number') {
        return this.sortDirection === 'asc' ? aValue - bValue : bValue - aValue;
      }

      // Fallback to string comparison
      const comparison = String(aValue).localeCompare(
        String(bValue),
        undefined,
        { numeric: true }
      );
      return this.sortDirection === 'asc' ? comparison : -comparison;
    });
  }

  /** Handle column header click for sorting */
  onColumnSort(columnKey: string): void {
    // Don't sort menu column or empty key
    if (columnKey === 'menu' || columnKey === '') return;

    // Check if column is sortable
    const column = this.columns.find(col => col.key === columnKey);
    if (column && column.sortable === false) return;

    // If server-side sorting is enabled, emit event to parent instead of sorting locally
    if (this.serverSideSorting) {
      const newDirection = this.currentSortColumn === columnKey && this.currentSortDirection === 'asc' ? 'desc' : 'asc';
      this.sortChange.emit({ column: columnKey, direction: newDirection });
      return;
    }

    if (this.sortedColumn === columnKey) {
      // Toggle sort direction
      this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
    } else {
      // New column, start with ascending
      this.sortedColumn = columnKey;
      this.sortDirection = 'asc';
    }

    // Reset to first page when sorting
    this.currentPage = 1;
    if (this.serverSidePagination) {
      // Server-side pagination: rows are already paginated by server
      this.paginatedRows = this.rows;
    } else {
      // Client-side pagination: paginate locally
      this.updatePagination();
    }
    this.cdr.markForCheck();
  }

  /** Handle entries per page change */
  onEntriesChange(): void {
    this.entriesPerPage = this.selectedEntries;
    this.currentPage = 1; // Reset to first page
    this.updatePagination();
    this.cdr.markForCheck();
  }

  ngOnChanges(changes: SimpleChanges): void {
    // Update pagination when rows change
    if (changes['rows']) {
      // Reset to first page when rows change
      this.currentPage = 1;
      if (this.showPagination) {
        if (this.serverSidePagination) {
          this.paginatedRows = this.flattenRowsWithChildren(this.rows);
        } else {
          // Client-side pagination: paginate locally
          this.updatePagination();
        }
      } else {
        // When pagination is disabled, show all rows (sorted) with child rows
        const sortedParentRows = this.getSortedRows();
        this.paginatedRows = [];

        if (this.autoExpandAllRows && this.enableExpandableRows) {
          sortedParentRows.forEach((row) => {
            const rowKey = this.getRowKey(row);
            this.expandedRows.add(rowKey);
          });
        }

        sortedParentRows.forEach((row) => {
          this.paginatedRows.push(row);

          if (this.enableExpandableRows && !this.expandDetailTemplate) {
            const rowKey = this.getRowKey(row);
            const isExpanded = this.expandedRows.has(rowKey);
            const childRows = row['_childRows'] as TableRow[] | undefined;
            if (childRows && childRows.length > 0 && isExpanded) {
              this.paginatedRows.push(...childRows);
            }
          } else if (!this.enableExpandableRows) {
            const childRows = row['_childRows'] as TableRow[] | undefined;
            if (childRows && childRows.length > 0) {
              this.paginatedRows.push(...childRows);
            }
          }
        });
      }
      this.cdr.markForCheck();
    }

    // Auto-expand all rows when autoExpandAllRows changes or rows change
    if (
      (changes['autoExpandAllRows'] || changes['rows']) &&
      this.autoExpandAllRows &&
      this.enableExpandableRows
    ) {
      this.expandAllRows();
    }
  }

  /** Handle page change */
  changePage(newPage: number) {
    if (this.serverSidePagination) {
      // Server-side pagination: emit event to parent
      if (newPage < 1 || newPage > this.serverTotalPages()) return;
      this.pageChange.emit(newPage);
    } else {
      // Client-side pagination: handle locally
      if (newPage < 1 || newPage > this.totalPages()) return;
      this.currentPage = newPage;
      this.updatePagination();
    }
    this.scrollTableToTop();
  }

  /** Scroll the table container to top (used on page/size change) */
  private scrollTableToTop(): void {
    this.tableContainer?.nativeElement?.scrollTo({ top: 0, behavior: 'smooth' });
  }

  /** Calculate total pages */
  totalPages(): number {
    if (this.serverSidePagination) {
      return this.serverTotalPages();
    }
    return Math.ceil(this.rows.length / this.entriesPerPage);
  }

  /** Calculate total pages for server-side pagination */
  serverTotalPages(): number {
    const size = this.serverPageSize;
    if (size == null || size <= 0) return 0;
    return Math.ceil(this.serverTotalRecords / size);
  }

  /** Calculate total records */
  getTotalRecords(): number {
    if (this.serverSidePagination) {
      return this.serverTotalRecords;
    }
    return this.rows.length;
  }

  /** Handle page size change */
  onPageSizeChange(newSize: number): void {
    if (this.serverSidePagination) {
      // Server-side pagination: emit event to parent
      this.pageSizeChange.emit(newSize);
    } else {
      // Client-side pagination: handle locally
      this.selectedEntries = newSize;
      this.entriesPerPage = newSize;
      this.currentPage = 1;
      this.updatePagination();
    }
    this.scrollTableToTop();
  }

  /** Handle page size change from template */
  handlePageSizeChange(newSize: number): void {
    if (this.serverSidePagination) {
      this.onPageSizeChange(newSize);
    } else {
      this.selectedEntries = newSize;
      this.entriesPerPage = newSize;
      this.currentPage = 1;
      this.updatePagination();
    }
  }

  onMenuClick(row: TableRow) {
    console.log('Menu button clicked for:', row);
  }

  /**
   * Handle column header click for sorting
   */
  onSort(columnKey: string): void {
    // Don't sort menu column or empty key
    if (columnKey === 'menu' || columnKey === '') {
      return;
    }

    // Check if column is sortable
    const column = this.columns.find(col => col.key === columnKey);
    if (column && column.sortable === false) {
      return;
    }

    if (this.serverSideSorting) {
      // Server-side sorting: emit event to parent
      let newDirection: 'asc' | 'desc' = 'asc';

      if (this.currentSortColumn === columnKey) {
        // Toggle direction if same column
        newDirection = this.currentSortDirection === 'asc' ? 'desc' : 'asc';
      } else {
        // New column, default to ascending
        newDirection = 'asc';
      }

      this.sortChange.emit({ column: columnKey, direction: newDirection });
    } else {
      // Client-side sorting: handle locally
      if (this.sortedColumn === columnKey) {
        // Toggle direction if same column
        this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
      } else {
        // New column, default to ascending
        this.sortedColumn = columnKey;
        this.sortDirection = 'asc';
      }

      // Sort rows locally
      this.sortRows();
    }
  }

  /**
   * Sort rows locally (client-side sorting)
   */
  private sortRows(): void {
    if (!this.sortedColumn) return;

    this.rows = [...this.rows].sort((a, b) => {
      const aValue = a[this.sortedColumn!];
      const bValue = b[this.sortedColumn!];

      if (aValue === null || aValue === undefined) return 1;
      if (bValue === null || bValue === undefined) return -1;

      let comparison = 0;
      if (typeof aValue === 'string' && typeof bValue === 'string') {
        comparison = aValue.localeCompare(bValue);
      } else if (typeof aValue === 'number' && typeof bValue === 'number') {
        comparison = aValue - bValue;
      } else {
        comparison = String(aValue).localeCompare(String(bValue));
      }

      return this.sortDirection === 'asc' ? comparison : -comparison;
    });

    // Update pagination after sorting
    this.updatePagination();
  }

  /**
   * Get sort icon for column
   */
  getSortIcon(columnKey: string): string {
    if (this.serverSideSorting) {
      if (this.currentSortColumn === columnKey) {
        return this.currentSortDirection === 'asc' ? '↑' : '↓';
      }
      return '⇅';
    } else {
      if (this.sortedColumn === columnKey) {
        return this.sortDirection === 'asc' ? '↑' : '↓';
      }
      return '⇅';
    }
  }

  /**
   * Check if column is currently sorted
   */
  isColumnSorted(columnKey: string): boolean {
    if (this.serverSideSorting) {
      return this.currentSortColumn === columnKey;
    }
    return this.sortedColumn === columnKey;
  }

  /**
   * Get current sort direction for column (for server-side sorting)
   */
  getCurrentSortDirection(): 'asc' | 'desc' {
    if (this.serverSideSorting) {
      return this.currentSortDirection;
    }
    return this.sortDirection;
  }

  onEdit(row: any, index: number) {
    if (this.openMenuIndex === index) {
      this.openMenuIndex = null;
    }
    this.edit.emit(row); // Emit an event if needed
  }

  onDelete(row: any, index: number) {
    if (this.openMenuIndex === index) {
      this.openMenuIndex = null;
    }
    this.delete.emit(row); // Emit an event if needed
  }

  onToggleChange(event: Event) {
    console.log(event);
  }


  /**
   * Get badge color classes based on status value
   */
  getBadgeColor(status: string, columnKey?: string): string {
    if (!status || !status.trim()) return 'bg-gray-100 text-gray-800';

    const normalizedStatus = status.toLowerCase().trim();

    // Special handling for auditQuery column
    if (columnKey === 'auditQuery') {
      if (normalizedStatus === 'pending') {
        return 'bg-amber-100 text-amber-800';
      }
      if (normalizedStatus === 'awaiting auditor') {
        return 'bg-blue-100 text-blue-800';
      }
      if (
        normalizedStatus === 'send back' ||
        normalizedStatus === 'sent back'
      ) {
        return 'bg-red-100 text-red-800';
      }
    }

    // Green badges
    if (
      normalizedStatus === 'tally' ||
      normalizedStatus === 'completed' ||
      normalizedStatus === 'ok' ||
      normalizedStatus === 'active' ||
      normalizedStatus === 'connected' ||
      normalizedStatus === 'resolved' ||
      normalizedStatus === 'regular' ||
      normalizedStatus === 'verified' ||
      normalizedStatus === 'published' ||
      normalizedStatus === 'branch'
    ) {
      return 'bg-green-100 text-green-800';
    }

    // Red badges
    if (
      normalizedStatus === 'excess' ||
      normalizedStatus === 'shortage' ||
      normalizedStatus === 'issue' ||
      normalizedStatus === 'unresolved' ||
      normalizedStatus === 'deviation found' ||
      normalizedStatus === 'cancelled' ||
      normalizedStatus === 'unassigned' ||
      normalizedStatus === 'inactive' ||
      normalizedStatus === 'priority' ||
      normalizedStatus === 'send back' ||
      normalizedStatus === 'not ok' ||
      normalizedStatus === 'sent back' ||
      normalizedStatus === 'expired'
    ) {
      return 'bg-red-100 text-red-800';
    }

    // Yellow/Amber badges
    if (
      normalizedStatus === 'variance' ||
      normalizedStatus === 'pending' ||
      normalizedStatus === 'draft' ||
      normalizedStatus === 'incremental' ||
      normalizedStatus === 'in transit'
    ) {
      return 'bg-amber-100 text-amber-800';
    }

    // Blue badges
    if (
      normalizedStatus === 'in progress' ||
      normalizedStatus === 'awaiting auditor'
    ) {
      return 'bg-blue-100 text-blue-800';
    }

    // Gray badges for N.A. or similar
    if (
      normalizedStatus === 'n.a.' ||
      normalizedStatus === 'na' ||
      normalizedStatus === 'not applicable'
    ) {
      return 'bg-gray-100 text-gray-800';
    }

    // Default gray badge if status doesn't match any predefined
    return 'bg-gray-100 text-gray-800';
  }

  /**
   * Check if a value should be displayed as a badge (for status columns)
   */
  shouldShowBadge(column: TableColumn, value: any): boolean {
    const badgeKeys = [
      'result',
      'status',
      'loanStatus',
      'auditStatus',
      'lastAuditStatus',
      'packet',
      'auditQuery',
      'queryStatus',
      'currentStatus',
      'source',
      'typeStatus',
      'action',
    ];
    if (badgeKeys.includes(column.key)) {
      return (
        value !== null && value !== undefined && String(value).trim() !== ''
      );
    }
    return false;
  }

  /**
   * Handle dropdown field change (generic for any dropdown column)
   */
  onDropdownChange(row: TableRow, column: TableColumn, value: string | string[], index: number) {
    // Extract string value from dropdown (single select always returns string)
    const selectedValue = Array.isArray(value) ? value[0] : value;
    const fieldKey = column.key;

    if (!row.hasOwnProperty(fieldKey)) {
      row[fieldKey] = '';
    }
    row[fieldKey] = selectedValue;

    // Handle clearWhen - clear other fields when this field changes to a specific value
    if (column.clearWhen) {
      // This column triggers clearing of other columns
      // Find columns that should be cleared when this column has a specific value
      this.columns.forEach(col => {
        if (col.clearWhen?.field === fieldKey && selectedValue === col.clearWhen.value) {
          row[col.key] = '';
        }
      });
    }

    // Check if any other column should be cleared based on this field's new value
    this.columns.forEach(col => {
      if (col.clearWhen?.field === fieldKey && selectedValue === col.clearWhen.value) {
        row[col.key] = '';
      }
    });

    // Update result based on current row values
    this.updateResult(row);

    // Keep packet/response consumers in sync when Response is a dropdown
    if (fieldKey === 'response') {
      this.responseChange.emit({ row, value: selectedValue });
      this.validateRemarks(row, index);
    }

    // Emit generic field change event
    this.fieldChanged.emit({ row, field: fieldKey, value: selectedValue, index, column });

    this.cdr.detectChanges();
  }

  /**
   * Handle input field change (generic for any input column)
   */
  onInputChange(row: TableRow, column: TableColumn, value: string, index: number) {
    const fieldKey = column.key;

    if (!row.hasOwnProperty(fieldKey)) {
      row[fieldKey] = '';
    }
    row[fieldKey] = value;

    // Emit generic field change event
    this.fieldChanged.emit({ row, field: fieldKey, value, index, column });
  }

  /**
   * Check if a dropdown column should be disabled based on disableWhen config
   */
  isColumnDisabled(row: TableRow, column: TableColumn): boolean {
    if (this.readonly) return true;
    if (!column.disableWhen) return false;
    return row[column.disableWhen.field] === column.disableWhen.value;
  }

  /**
   * Get dropdown options for a column
   */
  getDropdownOptions(column: TableColumn): string[] {
    return column.dropdownOptions || [];
  }

  /**
   * Handle remarks input change
   */
  onRemarksChange(row: TableRow, value: string, index: number) {
    if (!row.hasOwnProperty('remarks')) {
      row['remarks'] = '';
    }
    row['remarks'] = value;

    // Validate remarks when response is "Not OK"
    this.validateRemarks(row, index);
    this.cdr.detectChanges();
  }

  /**
   * Validate remarks field - required for non-OK response statuses
   */
  validateRemarks(row: TableRow, index: number): void {
    const response = row['response'];
    const remarks = row['remarks'];

    // Clear existing errors for this row
    if (!this.validationErrors.has(index)) {
      this.validationErrors.set(index, {});
    }

    const remarksRequired =
      response === 'Not OK' ||
      response === 'Shortage' ||
      response === 'Sent to Auction';

    if (remarksRequired && (!remarks || String(remarks).trim() === '')) {
      this.validationErrors.get(index)!['remarks'] = 'Please enter your remarks';
    } else {
      delete this.validationErrors.get(index)!['remarks'];
      if (Object.keys(this.validationErrors.get(index)!).length === 0) {
        this.validationErrors.delete(index);
      }
    }
  }

  /**
   * Get validation error for a field in a row
   */
  getFieldError(rowIndex: number, field: string): string | null {
    const errors = this.validationErrors.get(rowIndex);
    return errors?.[field] || null;
  }

  /**
   * Check if a field has validation error
   */
  hasFieldError(rowIndex: number, field: string): boolean {
    return !!this.getFieldError(rowIndex, field);
  }

  /**
   * Validate all rows and return validation result
   */
  validateAllRows(): { isValid: boolean; errors: Array<{ rowIndex: number; field: string; message: string }> } {
    const errors: Array<{ rowIndex: number; field: string; message: string }> = [];

    this.paginatedRows.forEach((row, index) => {
      this.validateRemarks(row, index);
      const rowErrors = this.validationErrors.get(index);
      if (rowErrors) {
        Object.keys(rowErrors).forEach(field => {
          errors.push({
            rowIndex: index,
            field,
            message: rowErrors[field]
          });
        });
      }
    });

    return {
      isValid: errors.length === 0,
      errors
    };
  }

  /**
   * Handle response button change
   */
  onResponseChange(row: TableRow, value: string, index: number) {
    if (!row.hasOwnProperty('response')) {
      row['response'] = '';
    }
    // Toggle: if clicking the same button, deselect it
    row['response'] = row['response'] === value ? '' : value;
    this.responseChange.emit({ row, value: row['response'] });
    // Validate remarks when response changes
    this.validateRemarks(row, index);
    this.cdr.detectChanges();
  }

  /**
   * Handle items image click
   */
  onItemsImageClick(row: TableRow, index: number) {
    const imageUrl = row['itemsImage'] || '/assests/cctv-pic.jpg';
    this.itemsImageClick.emit({ row, imageUrl });
  }

  // Store current row for camera capture
  private currentCaptureRow: { row: TableRow; index: number } | null = null;

  /**
   * Open camera modal for image capture
   */
  onImageCapture(row: TableRow, index: number, event?: Event) {
    if (event) {
      event.stopPropagation();
    }

    // Store current row reference
    this.currentCaptureRow = { row, index };

    // Open camera modal
    if (this.cameraModal) {
      this.cameraModal.open();
    }
  }

  /**
   * Handle view image click
   */
  onViewImage(row: TableRow, index: number, event?: Event) {
    if (event) {
      event.stopPropagation();
    }

    // Emit event for parent component to handle image viewing
    this.viewImageClick.emit(row);
  }

  /**
   * Handle captured image from camera modal
   */
  onCameraImageCaptured(data: { file: File; dataUrl: string }) {
    if (this.currentCaptureRow) {
      const { row, index } = this.currentCaptureRow;

      // Store the captured image file and filename
      row['imageFile'] = data.file;
      row['imageFileName'] = data.file.name;
      row['imageDataUrl'] = data.dataUrl; // For preview if needed

      this.fieldChanged.emit({ row, field: 'image', value: data.file.name, index } as any);
      this.cdr.detectChanges();
      this.currentCaptureRow = null;
    }
  }

  /**
   * Handle camera modal cancelled
   */
  onCameraCancelled() {
    this.currentCaptureRow = null;
  }

  /**
   * Delete captured image
   */
  onDeleteImage(row: TableRow, index: number, event: Event) {
    event.stopPropagation(); // Prevent triggering camera modal
    row['imageFileName'] = '';
    row['imageFile'] = null;
    row['imageDataUrl'] = null;
    this.cdr.detectChanges();
  }

  /**
   * Update result badge based on dropdown values
   * Uses column configuration to determine logic
   */
  updateResult(row: TableRow) {
    // Find dropdown columns with disableWhen config (availability-like column)
    const availabilityCol = this.columns.find(c => c.type === 'dropdown' && !c.disableWhen);
    // Find dropdown column that gets disabled (status-like column)
    const statusCol = this.columns.find(c => c.type === 'dropdown' && c.disableWhen);

    if (!availabilityCol) return;

    const availability = row[availabilityCol.key] || '';
    const status = statusCol ? (row[statusCol.key] || '') : '';
    const notAvailableValue = statusCol?.disableWhen?.value || 'Not Available';

    // If availability matches disableWhen value, result is "Issue"
    if (availability === notAvailableValue) {
      row['result'] = 'Issue';
    }
    // If status is "Not Proper", result is "Issue"
    else if (status === 'Not Proper') {
      row['result'] = 'Issue';
    }
    // If status is "Proper", result is "OK"
    else if (status === 'Proper') {
      row['result'] = 'OK';
    }
    // If no status selected, clear result
    else {
      row['result'] = '';
    }
    // Trigger change detection to update the view
    this.cdr.detectChanges();
  }

  ngAfterViewInit(): void {
    // Table now uses auto layout, no need for manual width syncing
  }

  /**
   * Get the route for a column with linkRoute
   */
  getColumnRoute(column: TableColumn, row: TableRow): string | null {
    if (!column.linkRoute) {
      return null;
    }
    if (typeof column.linkRoute === 'function') {
      return column.linkRoute(row);
    }
    // Replace :id or other placeholders with actual row values
    return column.linkRoute.replace(/:(\w+)/g, (match, key) => {
      return row[key] || match;
    });
  }

  /**
   * Check if a column has a link route
   */
  hasLinkRoute(column: TableColumn): boolean {
    return !!column.linkRoute;
  }

  /**
   * Handle picView button click
   */
  onPicViewClick(row: TableRow, rowIndex: number) {
    const imageData =
      row['imageData'] || row['imageFileName'] || row['referencePhoto'];
    this.picViewClick.emit({ row, imageData, rowIndex });
  }

  onVideoViewClick(row: TableRow, rowIndex: number) {
    const videoData =
      row['videoData'] || row['videoFileName'] || row['referenceVideo'];
    this.videoViewClick.emit({ row, videoData, rowIndex });
  }

  getVideoIcon(): string {
    return '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" class="w-5 h-5"><path fill="currentColor" d="M17 10.5V7a1 1 0 0 0-1-1H4a1 1 0 0 0-1 1v10a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1v-3.5l4 4v-11z"/></svg>';
  }

  /**
   * Get eye icon SVG for picView button
   */
  getEyeIcon(): string {
    return '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" class="w-5 h-5"><path fill="currentColor" d="M11.5 18c4 0 7.46-2.22 9.24-5.5C18.96 9.22 15.5 7 11.5 7s-7.46 2.22-9.24 5.5C4.04 15.78 7.5 18 11.5 18m0-12c4.56 0 8.5 2.65 10.36 6.5C20 16.35 16.06 19 11.5 19S3 16.35 1.14 12.5C3 8.65 6.94 6 11.5 6m0 2C14 8 16 10 16 12.5S14 17 11.5 17S7 15 7 12.5S9 8 11.5 8m0 1A3.5 3.5 0 0 0 8 12.5a3.5 3.5 0 0 0 3.5 3.5a3.5 3.5 0 0 0 3.5-3.5A3.5 3.5 0 0 0 11.5 9"/></svg>';
  }

  /**
   * Get start audit icon SVG (play icon)
   */
  getStartAuditIcon(): string {
    return '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" class="w-5 h-5"><path fill="currentColor" d="M8 5v14l11-7z"/></svg>';
  }

  /**
   * Get resume icon SVG (for In Progress - Resume Audit)
   * Resume icon: play button with forward arrow
   */
  getResumeIcon(): string {
    return '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" class="w-5 h-5"><path fill="currentColor" d="M8 5v14l11-7zm11 0v14h2V5z"/></svg>';
  }

  /**
   * Handle start audit button click
   */
  onStartAuditClick(row: TableRow) {
    this.startAuditClick.emit(row);
  }

  /**
   * Handle view button click
   */
  onViewClick(row: TableRow) {
    this.viewClick.emit(row);
  }

  /**
   * Handle in progress audit button click
   */
  onInProgressAuditClick(row: TableRow) {
    this.inProgressAuditClick.emit(row);
  }

  /** Handle deploy button click */
  onDeployClick(row: TableRow) {
    this.deployClick.emit(row);
  }

  /** Handle update form button click */
  onUpdateFormClick(row: TableRow) {
    this.updateFormClick.emit(row);
  }

  /** Check if action is an update form action */
  isUpdateFormAction(action: string): boolean {
    if (!action) return false;
    const normalizedAction = action.toLowerCase().trim();
    return normalizedAction === 'update form';
  }

  /**
   * Handle audit checkbox change - auto-save when checked (for child rows)
   */
  onAuditCheckboxChange(
    row: TableRow,
    paginatedIndex: number,
    checked: boolean
  ): void {
    if (!this.enableExpandableRows || !this.isChildRow(row)) {
      return;
    }
    row['audit'] = checked;
    if (checked) {
      const parentIndex = this.getParentRowIndex(paginatedIndex);
      const childIndex = this.getChildRowIndex(paginatedIndex);
      const parentRow = this.paginatedRows[parentIndex];
      if (parentRow) {
        row['isSaved'] = true;
        this.saveChildRowClick.emit({
          parentRow,
          parentIndex,
          childRow: row,
          childIndex,
        });
      }
    }
  }

  /**
   * Get label for action button based on action value
   */
  getActionLabel(action: string): string {
    if (!action) return 'View';
    const normalizedAction = action.toLowerCase().trim();
    if (normalizedAction === 'view audit') {
      return 'View Audit';
    }
    if (normalizedAction === 'view only') {
      return 'View only';
    }
    if (normalizedAction === 'view') {
      return 'View';
    }
    return 'View';
  }

  /**
   * Get label for resume-style button (Resume vs Resume Audit) based on action value
   */
  getResumeActionLabel(action: string): string {
    if (!action) return 'Resume Audit';
    const normalizedAction = action.toLowerCase().trim();
    if (normalizedAction === 'resume') return 'Resume';
    return 'Resume Audit';
  }

  /**
   * Check if action is a view action
   */
  isViewAction(action: string): boolean {
    if (!action) return false;
    const normalizedAction = action.toLowerCase().trim();
    return (
      normalizedAction === 'view' ||
      normalizedAction === 'view only' ||
      normalizedAction === 'view audit' ||
      normalizedAction === 'view form'
    );
  }

  /** Emit addRowClick when Add Key column plus icon is clicked (parent can open modal). */
  onAddKeyColumnClick(parentRow: TableRow, paginatedParentIndex: number): void {
    if (!parentRow || this.isChildRow(parentRow)) {
      return;
    }
    this.addRowClick.emit({ parentRow, parentIndex: paginatedParentIndex });
    if (this.skipDefaultChildRow && this.enableExpandableRows) {
      const rowKey = this.getRowKey(parentRow);
      if (!this.expandedRows.has(rowKey)) {
        this.expandedRows.add(rowKey);
      }
      this.updatePagination();
      this.cdr.detectChanges();
    }
  }

  /** Add a new observation/image row for a parent row */
  addObservationImageRow(paginatedParentIndex: number): void {
    const parentRow = this.paginatedRows[paginatedParentIndex];
    if (!parentRow || this.isChildRow(parentRow)) {
      return;
    }
    this.addRowClick.emit({ parentRow, parentIndex: paginatedParentIndex });

    if (this.skipDefaultChildRow) {
      const rowKey = this.getRowKey(parentRow);
      if (!this.expandedRows.has(rowKey)) {
        this.expandedRows.add(rowKey);
      }
      this.updatePagination();
      this.cdr.detectChanges();
      return;
    }

    let newChildRow: TableRow;
    if (this.customChildRowFactory) {
      newChildRow = this.customChildRowFactory(parentRow);
      newChildRow['isChildRow'] = true;
      newChildRow['_parentRow'] = parentRow;
    } else {
      newChildRow = {
        observation: '',
        imageFileName: '',
        imageFile: null,
        imageDataUrl: null,
        hasImage: false,
        isChildRow: true,
        _parentRow: parentRow,
      };
    }

    if (!parentRow['_childRows']) {
      parentRow['_childRows'] = [];
    }
    const childRows = parentRow['_childRows'] as TableRow[];
    childRows.push(newChildRow);

    const rowKey = this.getRowKey(parentRow);
    if (!this.expandedRows.has(rowKey)) {
      this.expandedRows.add(rowKey);
    }
    this.updatePagination();
    this.cdr.detectChanges();
  }

  /** Remove an observation/image row */
  removeObservationImageRow(
    paginatedParentIndex: number,
    childRowIndex: number
  ): void {
    const parentRow = this.paginatedRows[paginatedParentIndex];
    if (!parentRow || this.isChildRow(parentRow)) {
      return;
    }
    const childRows = parentRow['_childRows'] as TableRow[] | undefined;
    const childRow = childRows?.[childRowIndex];

    if (this.confirmBeforeRemoveChildRow) {
      this.removeRowClick.emit({
        parentRow,
        parentIndex: paginatedParentIndex,
        childIndex: childRowIndex,
        childRow: childRow ?? undefined,
      });
      return;
    }

    this.removeRowClick.emit({
      parentRow,
      parentIndex: paginatedParentIndex,
      childIndex: childRowIndex,
    });
    if (childRows && childRows.length > childRowIndex) {
      childRows.splice(childRowIndex, 1);
      this.updatePagination();
      this.cdr.detectChanges();
    }
  }

  /** Check if a row is a child row */
  isChildRow(row: TableRow): boolean {
    return row['isChildRow'] === true;
  }

  /** Get parent row index from paginated row index */
  getParentRowIndex(paginatedIndex: number): number {
    const row = this.paginatedRows[paginatedIndex];
    if (!row || !this.isChildRow(row)) {
      return paginatedIndex;
    }
    for (let i = paginatedIndex - 1; i >= 0; i--) {
      const prevRow = this.paginatedRows[i];
      if (!this.isChildRow(prevRow)) {
        return i;
      }
    }
    return 0;
  }

  /** Get child row index from paginated row index */
  getChildRowIndex(paginatedIndex: number): number {
    const row = this.paginatedRows[paginatedIndex];
    if (!row || !this.isChildRow(row)) {
      return -1;
    }
    const parentRow = row['_parentRow'] as TableRow;
    if (!parentRow) return -1;
    const childRows = parentRow['_childRows'] as TableRow[] | undefined;
    if (!childRows) return -1;
    return childRows.indexOf(row);
  }

  /** Check if variance column exists */
  hasVarianceColumn(): boolean {
    return this.columns.some((col) => col.key === 'variance');
  }

  /** Check if observation and image columns exist (for add/remove) */
  hasObservationImageColumns(): boolean {
    const hasObservation = this.columns.some((col) => col.key === 'observation');
    const hasImage = this.columns.some((col) => col.key === 'image');
    const hasStatutoryDocument = this.columns.some(
      (col) => col.key === 'statutoryDocument'
    );
    const hasParticularName = this.columns.some(
      (col) => col.key === 'particularName'
    );
    return (
      !!hasObservation &&
      !!hasImage &&
      !!hasStatutoryDocument &&
      !hasParticularName
    );
  }

  /** Check if statusKey column exists */
  hasStatusKeyColumn(): boolean {
    return this.columns.some((col) => col.key === 'statusKey');
  }

  /** Check if audit or auditRemark columns exist */
  hasAuditColumns(): boolean {
    const childCols = this.childRowColumns || this.columns;
    return childCols.some(
      (col) => col.key === 'audit' || col.key === 'auditRemark'
    );
  }

  /** Get columns to use for child rows */
  getChildRowColumns(): TableColumn[] {
    return this.childRowColumns || this.columns;
  }

  /** True when row at index is the first child row after its parent (used for key table header). */
  isFirstChildRowInGroup(index: number): boolean {
    const row = this.paginatedRows[index];
    if (!row || !this.isChildRow(row)) return false;
    const prevRow = index > 0 ? this.paginatedRows[index - 1] : null;
    return !!prevRow && !this.isChildRow(prevRow);
  }

  /** Get child rows for a parent row (for nested child table rendering). */
  getChildRowsForParent(parentRow: TableRow): TableRow[] {
    const childRows = parentRow['_childRows'] as TableRow[] | undefined;
    return childRows ?? [];
  }

  /** Check if the next row belongs to the same parent */
  isNextRowSameParent(currentIndex: number): boolean {
    const currentRow = this.paginatedRows[currentIndex];
    const nextRow = this.paginatedRows[currentIndex + 1];
    if (!currentRow || !nextRow) return false;
    if (!this.isChildRow(currentRow) && this.isChildRow(nextRow)) {
      const childRows = currentRow['_childRows'] as TableRow[] | undefined;
      if (childRows && childRows.includes(nextRow)) return true;
    }
    if (this.isChildRow(currentRow) && this.isChildRow(nextRow)) {
      const currentParent = currentRow['_parentRow'] as TableRow;
      const nextParent = nextRow['_parentRow'] as TableRow;
      if (
        currentParent &&
        nextParent &&
        currentParent === nextParent
      ) {
        return true;
      }
    }
    return false;
  }

  /**
   * Check if menu should be shown
   * - If showEdit is true: only checks editPermission
   * - If showMenu is true: checks both editPermission and deletePermission
   * - If both are false: menu is hidden
   */
  shouldShowMenu(): boolean {
    // If both showMenu and showEdit are false, don't show menu
    if (!this.showMenu && !this.showEdit) {
      return false;
    }

    // If showEdit is true, only check editPermission
    if (this.showEdit) {
      const hasEditPermissionSet =
        this.editPermission && this.editPermission.trim() !== '';

      // If no edit permission is set, show menu (backward compatibility)
      if (!hasEditPermissionSet) {
        return true;
      }

      // Check if edit permission is available
      return this.authService.hasPermission(this.editPermission!);
    }

    // If showMenu is true, check both edit and delete permissions
    if (this.showMenu) {
      const hasEditPermissionSet =
        this.editPermission && this.editPermission.trim() !== '';
      const hasDeletePermissionSet =
        this.deletePermission && this.deletePermission.trim() !== '';

      // If no permissions are set, show menu (backward compatibility)
      if (!hasEditPermissionSet && !hasDeletePermissionSet) {
        return true;
      }

      // Check if at least one permission is available
      const canEdit =
        !hasEditPermissionSet ||
        this.authService.hasPermission(this.editPermission!);
      const canDelete =
        !hasDeletePermissionSet ||
        this.authService.hasPermission(this.deletePermission!);

      return canEdit || canDelete;
    }

    return false;
  }

  /**
   * Get menu actions for a specific row
   * Returns which actions (edit/delete) should be shown for the row
   */
  getRowMenuActions(row: TableRow): { showEdit: boolean; showDelete: boolean } {
    // When readonly, show menu for specific rows (e.g. pending vaults) with both edit and delete
    if (this.readonly && this.showMenuWhenReadonly?.(row)) {
      return { showEdit: true, showDelete: true };
    }
    // If custom function is provided, use it (highest priority)
    if (this.getMenuActions) {
      return this.getMenuActions(row);
    }
    // If showEdit is true, show only edit (no delete)
    if (this.showEdit) {
      return { showEdit: true, showDelete: false };
    }
    // If showMenu is true, show edit and delete based on inputs
    if (this.showMenu) {
      return { showEdit: true, showDelete: this.showDelete };
    }
    return { showEdit: true, showDelete: this.showDelete };
  }

  /** Effective edit permission for this row (child rows use childEditPermission when set). */
  getEditPermissionForRow(row: TableRow): string | undefined {
    if (this.isChildRow(row) && this.childEditPermission !== undefined) {
      return this.childEditPermission;
    }
    return this.editPermission;
  }

  /** Effective delete permission for this row (child rows use childDeletePermission when set). */
  getDeletePermissionForRow(row: TableRow): string | undefined {
    if (this.isChildRow(row) && this.childDeletePermission !== undefined) {
      return this.childDeletePermission;
    }
    return this.deletePermission;
  }

  /**
   * Handle search change from search component
   */
  onSearchChange(searchTerm: string): void {
    this.searchChange.emit(searchTerm);
  }

  /** Default character count for tooltip columns (remarks, audit comment, etc.) when showTooltip is true */
  readonly TOOLTIP_TRUNCATE_LENGTH = 50;

  /**
   * Return the display value for an empty cell. Status column (registerStatus) shows 'NA', others show '-'.
   */
  getCellDisplayValue(row: TableRow, column: TableColumn): string {
    const val = row[column.key];
    const str = val != null && val !== '' ? String(val).trim() : '';
    if (str === '') return column.key === 'registerStatus' ? 'NA' : '-';
    return str;
  }

  /**
   * Get display text for tooltip columns: truncated to N chars (default 50) when column.showTooltip is true.
   * Use this in the cell; pass full text to showTooltip() on hover.
   */
  getTruncatedDisplayText(value: string | null | undefined, column: TableColumn): string {
    const emptyDisplay = column.key === 'registerStatus' ? 'NA' : '-';
    if (value == null || value === '') return emptyDisplay;
    const str = String(value).trim();
    if (!column.showTooltip || str.length === 0) return str || emptyDisplay;
    const maxLen = column.tooltipTruncateLength ?? this.TOOLTIP_TRUNCATE_LENGTH;
    if (str.length <= maxLen) return str;
    return str.slice(0, maxLen) + '...';
  }

  /**
   * Show tooltip on mouse enter
   */
  showTooltip(event: MouseEvent, text: string): void {
    if (!text || text.trim() === '') {
      return;
    }
    this.tooltipText = text;
    this.tooltipVisible = true;
    this.updateTooltipPosition(event);
    this.cdr.markForCheck();
  }

  /**
   * Hide tooltip on mouse leave
   */
  hideTooltip(): void {
    this.tooltipVisible = false;
    this.tooltipText = '';
    this.cdr.markForCheck();
  }

  /**
   * Update tooltip position on mouse move
   */
  updateTooltipPosition(event: MouseEvent): void {
    if (!this.tooltipVisible) {
      return;
    }
    // Position tooltip above the cursor, centered horizontally
    this.tooltipX = event.clientX;
    this.tooltipY = event.clientY - 10; // 10px above cursor
    this.cdr.markForCheck();
  }

  /**
   * Check if dropdown for a row should open upward based on table container height
   * Only rows nearest to the bottom of the table container will open upward
   */
  shouldOpenUpward(rowIndex: number): boolean {
    if (this.rowDropdownOpenUpward.has(rowIndex)) {
      return this.rowDropdownOpenUpward.get(rowIndex)!;
    }

    // Calculate based on table container, not viewport
    if (!this.tableContainer?.nativeElement) {
      return false;
    }

    const tableContainer = this.tableContainer.nativeElement;
    const containerRect = tableContainer.getBoundingClientRect();
    const containerHeight = containerRect.height;
    const containerTop = containerRect.top;
    const containerBottom = containerRect.bottom;

    // Get the row element
    const tbody = tableContainer.querySelector('tbody');
    if (!tbody) return false;

    const rows = tbody.querySelectorAll('tr');
    if (rowIndex >= rows.length) return false;

    const row = rows[rowIndex] as HTMLElement;
    const rowRect = row.getBoundingClientRect();
    const rowBottom = rowRect.bottom;

    // Calculate threshold: if row's bottom is within 35% of container bottom, open upward
    const bottomThresholdPercent = 0.35;
    const threshold = containerBottom - (containerHeight * bottomThresholdPercent);

    // Only open upward if row's bottom is below the threshold (near the bottom)
    const shouldOpenUp = rowBottom > threshold;
    this.rowDropdownOpenUpward.set(rowIndex, shouldOpenUp);
    return shouldOpenUp;
  }

  /**
   * Recalculate dropdown directions when dropdown opens
   */
  onDropdownOpen(rowIndex: number): void {
    // Clear cache for this row and recalculate
    this.rowDropdownOpenUpward.delete(rowIndex);
    setTimeout(() => {
      this.shouldOpenUpward(rowIndex);
      this.cdr.markForCheck();
    }, 0);
  }
}
